import React, { useEffect, useRef, useState } from 'react';
import './styles/LayerSlideNumber.scss';
import type { LayerSwitchDirection } from '../context/LayerSwitchContext';
import { LAYER_SLIDE_MS } from '../constants/layerSlideAnimation';

type SlidePhase = 'idle' | 'exit-up' | 'exit-down' | 'enter-from-below' | 'enter-from-above';

interface TimeMultBaseSlot {
  visible: boolean;
  negative: boolean;
}

interface LayerSlideTimeMultBaseProps {
  visible: boolean;
  negative: boolean;
  direction: LayerSwitchDirection;
  switchNonce: number;
  slideEnabled?: boolean;
  className?: string;
}

function slideClass(phase: SlidePhase): string {
  return phase === 'idle' ? '' : `layer-slide-${phase}`;
}

function slotText(slot: TimeMultBaseSlot): string | null {
  if (!slot.visible) return null;
  return slot.negative ? '-60' : '60';
}

/** 時刻レイヤーの multiplier 行頭「60」— レイヤー切替で縦スライド（×N と同型） */
export const LayerSlideTimeMultBase: React.FC<LayerSlideTimeMultBaseProps> = ({
  visible,
  negative,
  direction,
  switchNonce,
  slideEnabled = true,
  className = '',
}) => {
  const [displaySlot, setDisplaySlot] = useState<TimeMultBaseSlot>({ visible, negative });
  const [animPhase, setAnimPhase] = useState<SlidePhase>('idle');
  const committedNonce = useRef(switchNonce);
  const pendingRef = useRef<TimeMultBaseSlot>({ visible, negative });
  pendingRef.current = { visible, negative };
  const isAnimating = animPhase !== 'idle';
  const slidePending = direction !== 0 && switchNonce === committedNonce.current;
  const slideActive = switchNonce !== committedNonce.current || isAnimating;
  const layerSlideHiding = slideActive || slidePending;
  const phaseClass = slideActive ? slideClass(animPhase) : '';
  const text = slotText(displaySlot);

  useEffect(() => {
    const pending = direction !== 0 && switchNonce === committedNonce.current;
    if (!slideEnabled && !pending && switchNonce === committedNonce.current) {
      committedNonce.current = switchNonce;
      return;
    }
    if (switchNonce === committedNonce.current) return;

    if (!slideEnabled) {
      setDisplaySlot(pendingRef.current);
      setAnimPhase('idle');
      committedNonce.current = switchNonce;
      return;
    }

    if (direction === 0) {
      setDisplaySlot(pendingRef.current);
      setAnimPhase('idle');
      committedNonce.current = switchNonce;
      return;
    }

    const exitPhase: SlidePhase = direction === 1 ? 'exit-up' : 'exit-down';
    const enterPhase: SlidePhase = direction === 1 ? 'enter-from-below' : 'enter-from-above';
    const targetNonce = switchNonce;

    setAnimPhase(exitPhase);

    const tEnter = window.setTimeout(() => {
      setDisplaySlot(pendingRef.current);
      setAnimPhase(enterPhase);
    }, LAYER_SLIDE_MS);

    const tIdle = window.setTimeout(() => {
      setAnimPhase('idle');
      committedNonce.current = targetNonce;
    }, LAYER_SLIDE_MS * 2);

    return () => {
      window.clearTimeout(tEnter);
      window.clearTimeout(tIdle);
    };
  }, [switchNonce, direction, slideEnabled]);

  useEffect(() => {
    if (slidePending || slideActive) return;
    setDisplaySlot({ visible, negative });
  }, [visible, negative, switchNonce, slidePending, slideActive]);

  const showContent =
    text !== null ||
    (layerSlideHiding && (animPhase === 'exit-up' || animPhase === 'exit-down'));

  if (!showContent) {
    return null;
  }

  const wrapperClass = [
    'layer-slide-formatted-value',
    'time-mult-base',
    displaySlot.negative ? 'negative' : 'positive',
    className,
    phaseClass,
  ]
    .filter(Boolean)
    .join(' ');

  return <span className={wrapperClass}>{text ?? slotText(displaySlot)}&nbsp;</span>;
};
